import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { formatEnglishDate } from "./utils/dateFormatting";
import logo from "./LogoAppleTreeHD.svg";
import { getIpfsUrl } from "./utils/ipfs";

const ReportPage = () => {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("cid");

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cid) {
      setError("No CID provided in query string");
      setLoading(false);
      return;
    }

    fetchReport();
  }, [cid]);

  const fetchReport = async () => {
    setLoading(true);
    setError("");

    try {
      const url = getIpfsUrl(cid, 'report.json');
      console.log("[ReportPage] Fetching from:", url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("[ReportPage] Report loaded:", data);
      setReport(data);
    } catch (err) {
      console.error("[ReportPage] Error:", err);
      setError(err.message || "Failed to load report from IPFS");
    } finally {
      setLoading(false);
    }
  };

  const renderScoreBar = (score, maxScore = 100) => {
    const percentage = (score / maxScore) * 100;
    let color = "#ef4444"; // red
    if (percentage >= 80) color = "#22c55e"; // green
    else if (percentage >= 60) color = "#eab308"; // yellow
    else if (percentage >= 40) color = "#f97316"; // orange

    return (
      <div style={styles.scoreBarContainer}>
        <div style={styles.scoreBarBg}>
          <div
            style={{
              ...styles.scoreBarFill,
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: color,
            }}
          />
        </div>
        <span style={styles.scoreBarLabel}>{score}</span>
      </div>
    );
  };

  const shouldShowAddress = () => {
    return report?.mapVisibility === "EXACT_ADDRESS" && report?.address;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/esg-dashboard">
            <img src={logo} alt="AppleTree" style={styles.logo} />
          </Link>
        </div>
        <div style={styles.loading}>Loading report from IPFS...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/esg-dashboard">
            <img src={logo} alt="AppleTree" style={styles.logo} />
          </Link>
        </div>
        <div style={styles.error}>
          <h2>Error Loading Report</h2>
          <p>{error}</p>
          <p style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
            CID: {cid}
          </p>
          <Link to="/esg-dashboard" style={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/esg-dashboard">
            <img src={logo} alt="AppleTree" style={styles.logo} />
          </Link>
        </div>
        <div style={styles.error}>No report data found</div>
      </div>
    );
  }

  const rawJsonUrl = getIpfsUrl(cid, 'report.json');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Link to="/esg-dashboard">
          <img src={logo} alt="AppleTree" style={styles.logo} />
        </Link>
        <h1 style={styles.title}>ESG Certification Report</h1>
      </div>

      <div style={styles.content}>
        {/* Company Info */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Company Information</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Company Name:</span>
              <span style={styles.infoValue}>
                {report.companyName || report.name || "N/A"}
              </span>
            </div>
            {shouldShowAddress() && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Address:</span>
                <span style={styles.infoValue}>{report.address}</span>
              </div>
            )}
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Issued:</span>
              <span style={styles.infoValue}>{formatEnglishDate(report.issuedAt)}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Expires:</span>
              <span style={styles.infoValue}>{formatEnglishDate(report.expiresAt)}</span>
            </div>
          </div>
        </div>

        {/* ESG Score Breakdown */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>ESG Score Breakdown</h2>
          
          <div style={styles.scoreSection}>
            <div style={styles.scoreHeader}>
              <span style={styles.scoreLetter}>E</span>
              <span style={styles.scoreTitle}>Environmental</span>
            </div>
            {renderScoreBar(report.scoreE || report.environmentalScore || 0)}
          </div>

          <div style={styles.scoreSection}>
            <div style={styles.scoreHeader}>
              <span style={styles.scoreLetter}>S</span>
              <span style={styles.scoreTitle}>Social</span>
            </div>
            {renderScoreBar(report.scoreS || report.socialScore || 0)}
          </div>

          <div style={styles.scoreSection}>
            <div style={styles.scoreHeader}>
              <span style={styles.scoreLetter}>G</span>
              <span style={styles.scoreTitle}>Governance</span>
            </div>
            {renderScoreBar(report.scoreG || report.governanceScore || 0)}
          </div>

          <div style={styles.totalScoreSection}>
            <span style={styles.totalScoreLabel}>Total ESG Score</span>
            <span style={styles.totalScoreValue}>
              {report.scoreTotal || report.totalScore || report.score || "N/A"}
            </span>
          </div>
        </div>

        {/* Section Scores (if available) */}
        {report.sectionScores && Object.keys(report.sectionScores).length > 0 && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Section Scores</h2>
            {Object.entries(report.sectionScores).map(([section, score]) => (
              <div key={section} style={styles.sectionScoreRow}>
                <span style={styles.sectionLabel}>{section}</span>
                {renderScoreBar(score)}
              </div>
            ))}
          </div>
        )}

        {/* Certification Details */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Certification Details</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Report CID:</span>
              <span style={styles.infoValue} title={cid}>
                {cid.substring(0, 20)}...{cid.substring(cid.length - 10)}
              </span>
            </div>
            {report.companyWallet && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Company Wallet:</span>
                <span style={styles.infoValue}>
                  {report.companyWallet.substring(0, 10)}...
                  {report.companyWallet.substring(report.companyWallet.length - 8)}
                </span>
              </div>
            )}
            {report.txHash && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Blockchain TX:</span>
                <a
                  href={`https://etherscan.io/tx/${report.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  View on Etherscan →
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div style={styles.footer}>
          <a
            href={getIpfsUrl(cid, 'report.pdf')}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.downloadPdfButton}
          >
            📄 Download PDF
          </a>
          <div style={styles.secondaryLinks}>
            <a
              href={rawJsonUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.rawJsonLink}
            >
              Raw JSON
            </a>
            <Link to="/esg-dashboard" style={styles.backLink}>
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    padding: "20px",
    color: "#f5f5f5",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
  },
  logo: {
    maxWidth: "150px",
    marginBottom: "10px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#7CFFB2",
    margin: "10px 0",
  },
  content: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "20px",
    border: "1px solid rgba(124, 255, 178, 0.2)",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#7CFFB2",
    marginBottom: "20px",
    borderBottom: "2px solid rgba(124, 255, 178, 0.3)",
    paddingBottom: "10px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  infoLabel: {
    fontSize: "13px",
    color: "#94a3b8",
    fontWeight: "500",
  },
  infoValue: {
    fontSize: "15px",
    color: "#f1f5f9",
    fontWeight: "600",
  },
  scoreSection: {
    marginBottom: "20px",
  },
  scoreHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
  },
  scoreLetter: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#7CFFB2",
    width: "30px",
  },
  scoreTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#e2e8f0",
  },
  scoreBarContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  scoreBarBg: {
    flex: 1,
    height: "24px",
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    borderRadius: "12px",
    overflow: "hidden",
    border: "1px solid rgba(148, 163, 184, 0.3)",
  },
  scoreBarFill: {
    height: "100%",
    transition: "width 0.3s ease",
    borderRadius: "12px",
  },
  scoreBarLabel: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#7CFFB2",
    minWidth: "40px",
    textAlign: "right",
  },
  totalScoreSection: {
    marginTop: "30px",
    padding: "20px",
    background: "linear-gradient(135deg, rgba(124, 255, 178, 0.15), rgba(34, 197, 94, 0.15))",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "2px solid rgba(124, 255, 178, 0.4)",
  },
  totalScoreLabel: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#f1f5f9",
  },
  totalScoreValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#7CFFB2",
  },
  sectionScoreRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "12px",
  },
  sectionLabel: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#cbd5e1",
    minWidth: "120px",
  },
  footer: {
    marginTop: "30px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  downloadPdfButton: {
    padding: "16px 24px",
    backgroundColor: "rgba(124, 255, 178, 0.9)",
    color: "#020617",
    textDecoration: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "700",
    textAlign: "center",
    border: "2px solid rgba(124, 255, 178, 1)",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px rgba(124, 255, 178, 0.3)",
    display: "block",
  },
  secondaryLinks: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  rawJsonLink: {
    padding: "8px 12px",
    color: "#60a5fa",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "500",
    transition: "all 0.2s",
  },
  backLink: {
    padding: "8px 12px",
    color: "#7CFFB2",
    textDecoration: "none",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  backButton: {
    padding: "10px 16px",
    backgroundColor: "rgba(124, 255, 178, 0.2)",
    color: "#7CFFB2",
    textDecoration: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    border: "1px solid rgba(124, 255, 178, 0.3)",
    transition: "all 0.2s",
  },
  backLink: {
    color: "#7CFFB2",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "600",
  },
  link: {
    color: "#60a5fa",
    textDecoration: "none",
    fontWeight: "600",
  },
  loading: {
    textAlign: "center",
    fontSize: "18px",
    color: "#94a3b8",
    padding: "60px 20px",
  },
  error: {
    textAlign: "center",
    padding: "40px 20px",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    borderRadius: "12px",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    maxWidth: "600px",
    margin: "0 auto",
  },
};

export default ReportPage;
